/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";

import { SubmitButton as Button } from "~/components/form/submit-button";
import { formatDate } from "~/utils/basic";
import { toast } from "react-toastify";
import { EditTaskStatus } from "~/services/supervisor.services";

interface ISupervisorTableProps {
  data: any[];
}

const SupervisorTable = ({ data }: ISupervisorTableProps) => {
  // console.log("🔍 TaskId type check →", typeof data?.[0]?.TaskId, data?.[0]?.TaskId);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "TaskId",
    direction: "desc",
  });
  // const [filteredData, setFilteredData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>(data);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<Set<any>>(new Set());

  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({
    TaskId: "",
    TaskName: "",
    Status: "",
    UserName: "",
    ReceivedOnServer: "",
    AllocatedOn: "",
    KeyedOn: "",
    FileProcessedDateTime: "",
    AHT: "",
  });

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const tableData = useMemo(() => data, [data]);
  const columnHelper = createColumnHelper<any>();

  const handleRowSelection = (id: any) => {
    setSelectedRows((prev) => {
      const newSelectedRows = new Set(prev);
      if (newSelectedRows.has(id)) {
        newSelectedRows.delete(id);
      } else {
        newSelectedRows.add(id);
      }
      return newSelectedRows;
    });
  };
  const handleSelectAll = (selectAll: boolean) => {
    if (selectAll) {
      const allIds = new Set(filteredData.map((row) => row.TaskId)); // Assuming TaskId is unique
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleSearchChange = useCallback(
    debounce((key: string, value: string) => {
      setSearchTerms((prev) => ({
        ...prev,
        [key]: value,
      }));
    }, 0),
    []
  );

  const requestSort = useCallback(
    (key: string) => {
      let direction: "asc" | "desc" = "asc";
      if (sortConfig.key === key && sortConfig.direction === "asc") {
        direction = "desc";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const sortedData = useMemo(() => {
    const sortableItems = [...filteredData];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] === "" || a[sortConfig.key] == null) {
          return 1;
        }
        if (b[sortConfig.key] === "" || b[sortConfig.key] == null) {
          return -1;
        }
           
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];
      const isNumeric = !isNaN(valA) && !isNaN(valB);
      const compareA = isNumeric ? Number(valA) : valA;
      const compareB = isNumeric ? Number(valB) : valB;

      if (compareA < compareB) return sortConfig.direction === "asc" ? -1 : 1;
      if (compareA > compareB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }
    return sortableItems;
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    if (rowsPerPage === -1) return sortedData;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  const columns = useMemo(() => {
    const selectColumnHeader = (
      <input
        type="checkbox"
        checked={filteredData.every((row) => selectedRows.has(row.TaskId))}
        onChange={(e) => handleSelectAll(e.target.checked)}
      />
    );

    const selectColumnCell = (props: any) => (
      <input
        type="checkbox"
        checked={selectedRows.has(props.row.original.TaskId)}
        onChange={() => handleRowSelection(props.row.original.TaskId)}
      />
    );

    const createColumn = (key: string) => {
      const columnHeader = (
        <div className="p-2">
          <div className="flex flex-col gap-2 justify-center">
            <div>
              {key === "AHT" ? key : key.replace(/([A-Z])/g, " $1").trim()}
            </div>
            <div className="flex items-center justify-center gap-2">
              <input
                ref={(el) => (inputRefs.current[key] = el)}
                type="text"
                value={searchTerms[key] || ""}
                onChange={(e) => handleSearchChange(key, e.target.value)}
                className={`border border-gray-300 rounded ${key === "TaskName" ? " w-[250px]" : " w-[160px]"}`}
              />
              <button onClick={() => requestSort(key)}>
                <ArrowsUpDownIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      );

      return columnHelper.accessor(key as any, {
        header: columnHeader as any,
        cell: (props: any) => {
          const value = props?.getValue();
          if (
            key === "ReceivedOnServer" ||
            key === "AllocatedOn" ||
            key === "KeyedOn" ||
            key === "FileProcessedDateTime"
          ) {
            return formatDate(value, "MM-dd-yyyy hh:mm:ss a"); // 12-hour format
          }
          return value;
        },
      });
    };

    return [
      columnHelper.display({
        id: "select",
        header: selectColumnHeader as any,
        cell: selectColumnCell,
      }),
      ...Object.keys(searchTerms).map(createColumn),
      columnHelper.accessor("Actions", {
        header: () => (
          <div
            className="p-2"
            style={{
              position: "sticky",
              right: 0,
              backgroundColor: "white",
              zIndex: 10,
            }}
          >
            <div className="flex gap-2 justify-center">
              <div>Actions</div>
            </div>
          </div>
        ),
        cell: (props: any) => (
          <div
            style={{
              position: "sticky",
              right: 0,
              backgroundColor: "white",
              zIndex: 10,
            }}
          >
            <Link
              to="/supervisor/worklist/view/$taskId"
              params={{ taskId: props?.row.original.TaskId }}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Button
                type="button"
                size="small"
                variant="secondary"
                className="text-primary ring-primary hover:bg-orange-100 active:bg-orange-200"
              >
                View
              </Button>
            </Link>
          </div>
        ),
      }),
    ];
  }, [
    columnHelper,
    paginatedData,
    requestSort,
    selectedRows,
    searchTerms,
    tableData,
  ]);

  useEffect(() => {
    const filtered = data.filter((row) =>
      Object.entries(searchTerms).every(([key, searchTerm]) => {
        if (!searchTerm) {
          return true;
        }

        if (
          [
            "ReceivedOnServer",
            "AllocatedOn",
            "KeyedOn",
            "FileProcessedDateTime",
          ].includes(key) &&
          row[key]
        ) {
          const formattedDate = formatDate(row[key], "MM-dd-yyyy hh:mm:ss a");
          return formattedDate.toLowerCase().includes(searchTerm.toLowerCase());
        } else {
          return row[key]
            ?.toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }
      })
    );

    setFilteredData(filtered);
    setCurrentPage(1);
    setSelectedRows(new Set());
  }, [searchTerms, data]);
  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const exportToExcel = () => {
    const selectedData = data.filter((row) => selectedRows.has(row.TaskId));

    if (selectedData.length === 0) {
      toast.error("No rows selected for export.", {
        theme: "colored",
      });
      return;
    }

    const headers = [
      "Task Id",
      "Task Name",
      "Status",
      "Agent",
      "Received On Server",
      "Allocated-On",
      "Keyed-On",
      "Processed Date Time",
      "AHT (Seconds)",
    ];

    const formatDateForExport = (
      dateValue: string | null | undefined
    ): string => {
      if (!dateValue) return "";
      const parsedDate = new Date(dateValue);
      return isNaN(parsedDate.getTime())
        ? ""
        : formatDate(parsedDate, "MM-dd-yyyy hh:mm:ss a");

      //return formatDate(value, "MM-dd-yyyy hh:mm:ss a"); // 12-hour format
    };

    const dataWithHeaders = [
      headers,
      ...selectedData.map((row) => [
        row.TaskId,
        row.TaskName,
        row.Status,
        row.UserName,
        formatDateForExport(row.ReceivedOnServer),
        formatDateForExport(row.AllocatedOn),
        formatDateForExport(row.KeyedOn),
        formatDateForExport(row.FileProcessedDateTime),
        row.AHT,
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Output data");
    XLSX.writeFile(workbook, "statusReport.xlsx");
  };

  const raiseTaskStatusUpdate = async (recieved: boolean) => {
    const selectedData = data.filter((row) => selectedRows.has(row.TaskId));
    if (selectedData.length === 0) {
      toast.error("No rows selected for updating task status", {
        theme: "colored",
      });
      return;
    }
    const transformedTaskData = selectedData
      .filter((task) =>
        recieved ? task.Status === "Hold" : task.Status === "Received"
      )
      .map((task) => ({ taskid: +task.TaskId }));

    //check if data selected is correct
    if (transformedTaskData.length === 0) {
      toast.error("Please select correct status tasks for status update", {
        theme: "colored",
      });
      return;
    }
    const transformedData = Object.values(transformedTaskData);
    console.log(
      "Data Selected for Status Update",
      selectedData,
      transformedData
    );
    try {
      const {
        data: response,
        status,
        error,
      } = await EditTaskStatus<typeof transformedData>(
        transformedData,
        recieved ? "received" : "hold"
      );
      if (status) {
        console.log((response as any).data);
        toast.success("Task Updated Successfully:", {
          theme: "colored",
        });
        window.location.reload();
      } else {
        throw new Error(error as string);
      }
    } catch (error) {
      console.error("Task Update Failed:", error);
      toast.error("Task Update Failed:" + error, {
        theme: "colored",
      });
    }
  };
  const enableSupervisorReceivedHoldButton = process.env.VITE_ENABLE_SUPERVISOR_RECEIVED_HOLD_BUTTON === "true";
  // console.log(`enableSupervisorReceivedHoldButton: ${process.env.VITE_ENABLE_SUPERVISOR_RECEIVED_HOLD_BUTTON}`);
  return (
    <div className="w-full">
      <div className="flex justify-between mb-4">
        <div>
          <label>
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => {
                const value =
                  e.target.value === "all" ? -1 : parseInt(e.target.value, 10);
                setRowsPerPage(value);
              }}
              className="border rounded p-1 pr-6"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={-1}>All</option>
            </select>
          </label>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={exportToExcel}
            className="bg-orange-500 text-white py-2 px-4 rounded"
          >
            Export to excel
          </button>
          {enableSupervisorReceivedHoldButton && (
            <>
              <button
                onClick={() => raiseTaskStatusUpdate(true)}
                className="bg-orange-500 text-white py-2 px-4 rounded"
              >
                Recieved
              </button>
              <button
                onClick={() => raiseTaskStatusUpdate(false)}
                className="bg-orange-500 text-white py-2 px-4 rounded"
              >
                Hold
              </button>
            </>
          )}
        </div>
      </div>
      <div className="w-full overflow-x-auto">
        <table
          className="min-w-full divide-y divide-gray-200"
          style={{ tableLayout: "fixed" }} // This is the inline style
        >
          <thead className="bg-gray-50 text-nowrap">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="text-sm">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="text-md">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`p-1 ${cell.column.id !== "TaskName" ? "text-center" : ""} 
                    ${cell.column.id === "TaskName" ? "break-words" : ""}`}
                    style={
                      cell.column.id === "Actions"
                        ? {
                            position: "sticky",
                            right: 0,
                            backgroundColor: "white",
                            zIndex: 10,
                          }
                        : cell.column.id === "TaskName"
                          ? { wordBreak: "break-word", whiteSpace: "normal" }
                          : {}
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div className="p-4">
          Showing {paginatedData.length} of {filteredData.length} entries
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setCurrentPage((prev) => Math.max(prev - 1, 1));
            }}
            disabled={currentPage === 1}
            className={`py-1 px-3 border rounded ${
              currentPage === 1 ? "bg-gray-200 cursor-not-allowed" : "bg-white"
            }`}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {Math.ceil(sortedData.length / rowsPerPage)}
          </span>
          <button
            onClick={() => {
              setCurrentPage((prev) => prev + 1);
            }}
            disabled={
              currentPage === Math.ceil(sortedData.length / rowsPerPage)
            }
            className={`py-1 px-3 border rounded ${
              currentPage === Math.ceil(sortedData.length / rowsPerPage)
                ? "bg-gray-200 cursor-not-allowed"
                : "bg-white"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupervisorTable;
